import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { getOnboarding } from '../api/endpoints/onboarding';
import { isOwner } from '../auth/roles';
import { useSession } from '../auth/useSession';
import { COUNTRY_TIMEZONE, countryOfZone } from '../lib/country';
import { bookingLinkUrl, consumerOrigin, linkMessage, whatsAppShareUrl } from '../lib/reservation-link';
import { useBusiness } from '../lib/useBusiness';
import { ReservationLinkBuilder, useCopyToClipboard } from './ReservationLinkBuilder';

/**
 * The reservation link, alive on Inicio for whoever attends the day (the owner, the front desk, a waiter, a
 * coach): the link of the business to send on WhatsApp or copy in one tap, and the builder that chooses the
 * service and the time right there, with no other page. The page /enlace stays for anybody who goes straight to it.
 */
export function ReservationLinkCard() {
  const business = useBusiness();
  const { session } = useSession();
  // The onboarding state is the owner's to read: a teammate never asks for it.
  const onboarding = useQuery({ queryKey: ['business-onboarding'], queryFn: getOnboarding, enabled: isOwner(session?.role) });
  const [building, setBuilding] = useState(false);
  const country = countryOfZone(business?.neighborhood ?? '');
  const url = business ? bookingLinkUrl(consumerOrigin(window.location.hostname, country), business.id) : '';
  const { copied, copy } = useCopyToClipboard(url);

  if (!business) return null;
  const hasServices = business.services.length > 0;
  const message = linkMessage({ businessName: business.name, timeZone: COUNTRY_TIMEZONE[country], url });

  return (
    <section className="section" aria-labelledby="enlace-inicio">
      <h2 className="section-label" id="enlace-inicio">
        Enlace de reserva
      </h2>
      <p className="row-sub enlace-lede">Mándalo por WhatsApp y te reservan directo, a la hora que tienes libre.</p>
      {onboarding.data?.state === 'draft' ? (
        <p className="notice" role="status">
          Tu negocio todavía no está publicado. El enlace abre cuando lo publiques desde <Link to="/primer-dia">Primer día</Link>.
        </p>
      ) : null}
      {building ? (
        <>
          <div className="enlace">
            <ReservationLinkBuilder business={business} />
          </div>
          <button type="button" className="btn small secondary" onClick={() => setBuilding(false)}>
            Cerrar
          </button>
        </>
      ) : (
        <>
          <div className="enlace-actions">
            <a className="btn small" href={whatsAppShareUrl('', message)} target="_blank" rel="noreferrer">
              Enviar por WhatsApp
            </a>
            <button type="button" className="btn small secondary" onClick={() => void copy()} aria-live="polite">
              {copied ? 'Copiado' : 'Copiar enlace'}
            </button>
            {hasServices ? (
              <button type="button" className="btn small secondary" aria-expanded={false} onClick={() => setBuilding(true)}>
                Armar con servicio y hora
              </button>
            ) : null}
          </div>
          {hasServices ? null : <p className="row-sub">Agrega un servicio para armar enlaces con hora.</p>}
        </>
      )}
    </section>
  );
}
